import { toComparableId } from 'features/Sku/Items/SkuMenuData.normalizers';

import { getModifierAvailabilityStatus, toModifierAvailability } from './Modifiers.availability';
import { namedOptions, toFormValues, toGroupFields, toOptionInput, visibleModifiers } from './Modifiers.formState';

export const planGroupFieldChanges = (loadedGroup, values) => {
  const stored = toGroupFields(toFormValues(loadedGroup));
  const next = toGroupFields(values);

  return Object.keys(next).filter(field => next[field] !== stored[field]);
};

const storedAsInput = stored => ({
  name: stored.core?.name,
  description: stored.core?.description || '',
  ordinal: stored.core?.ordinal,
  isDefault: Boolean(stored.core?.isDefault),
  ...toModifierAvailability(getModifierAvailabilityStatus(stored)),
});

// Ordinals never ride the per-option update: racy single-row writes could half-apply a reorder.
const optionHasChanged = (stored, input) => {
  const current = storedAsInput(stored);
  return Object.keys(input).some(field => field !== 'ordinal' && current[field] !== input[field]);
};

// Updating a group only writes group-level fields, so the options have to be reconciled one by one.
export const planOptionChanges = (loadedGroup, values) => {
  const unmatched = new Map(visibleModifiers(loadedGroup).map(modifier => [modifier.core?.id, modifier]));
  const created = [];
  const updated = [];

  namedOptions(values).forEach((option, index) => {
    const input = toOptionInput(option, index, values.rules);
    const stored = unmatched.get(option.core?.id);

    if (!stored) {
      created.push({ clientId: option.core?.id, modifier: input });
      return;
    }

    unmatched.delete(option.core.id);
    if (optionHasChanged(stored, input)) {
      const { ordinal, ...updates } = input;
      updated.push({ modifierId: option.core.id, updates });
    }
  });

  return { created, updated, removed: [...unmatched.keys()] };
};

export const planOrderChange = (loadedGroup, values) => {
  const storedIds = visibleModifiers(loadedGroup).map(modifier => modifier.core?.id);
  const formIds = namedOptions(values).map(option => option.core?.id);

  const storedSet = new Set(storedIds);
  const survivors = formIds.filter(id => storedSet.has(id));
  const storedSurvivors = storedIds.filter(id => formIds.includes(id));

  const survivorsMoved = survivors.some((id, index) => id !== storedSurvivors[index]);
  const newOnlyAtTail = formIds.slice(survivors.length).every(id => !storedSet.has(id));

  if (!survivorsMoved && newOnlyAtTail) return null;
  return formIds;
};

export const planItemLinks = (loadedGroup, values) => {
  const attachedIds = (loadedGroup?.attachedItems || []).map(item => item.itemId);
  const selectedIds = values.itemIds || [];

  return {
    linked: selectedIds.filter(itemId => !attachedIds.includes(itemId)),
    unlinked: attachedIds.filter(itemId => !selectedIds.includes(itemId)),
  };
};

export const attachedGroupIdsForItem = (groups, itemId) => {
  const comparableItemId = toComparableId(itemId);
  if (comparableItemId === null) return [];

  return (groups || [])
    .filter(group => (group.attachedItems || []).some(item => toComparableId(item.itemId) === comparableItemId))
    .map(group => group.core?.id)
    .filter(Boolean);
};

export const planGroupLinksForItem = ({ groups, itemId, selectedGroupIds }) => {
  const attachedIds = attachedGroupIdsForItem(groups, itemId);
  const selectedIds = selectedGroupIds || [];

  return {
    linked: selectedIds.filter(groupId => !attachedIds.includes(groupId)),
    unlinked: attachedIds.filter(groupId => !selectedIds.includes(groupId)),
  };
};
